import { useStep } from '@flywire/react-hooks';
import { cancelPayment, setCouponAdded } from 'actions';
import { Taxes } from 'components';
import { CloseModalConfirmation } from 'components/CloseModalConfirmation/CloseModalConfirmation';
import { PayerInformation } from 'components/PayerInformation/PayerInformation';
import { ReviewAndConfirm } from 'components/ReviewPayment/ReviewAndConfirm';
import { ReviewRecurringPayment } from 'components/ReviewPayment/ReviewRecurringPayment/ReviewRecurringPayment';
import { NOOP } from 'constants/actions';
import { Coupon } from 'containers/Coupon/Coupon';
import { Instructions as InstructionsContainer } from 'containers/Instructions/Instructions';
import { useModalContext } from 'hooks';
import { useState } from 'react';
import { type ConnectedProps, connect, useSelector } from 'react-redux';
import {
  isSelectedOfferRecurring,
  orderIncludesTaxes,
  paymentIncludesTaxes,
} from 'selectors';
import { isNonAutomatedRecurring } from 'selectors/entities/offers/offers';
import { getOrderOffer } from 'selectors/entities/order/order';
import {
  isCouponAdded,
  isCreditCardPaymentFlow,
  isPayingFromUS,
  shouldDisplayAddCoupon,
} from 'selectors/ui';
import type { RootState } from '../../reducers/types';
import { PaymentOptions } from '../PaymentOptions/PaymentOptions';

type PropsFromRedux = ConnectedProps<typeof connector>;
type PaymentStepsProps = PropsFromRedux & {
  onCancelPayment: () => void;
  onClose: () => void;
  onRestart: () => void;
  isSelectedOfferRecurring: boolean;
  isNonAutomatedRecurring: boolean;
};

const PaymentStepsComponent = ({
  isSelectedOfferRecurring = false,
  isNonAutomatedRecurring = false,
  displayTaxes = false,
  displayCoupon = false,
  isPayingFromUS,
  onCancelPayment = NOOP,
  onClose = NOOP,
  onRestart = NOOP,
  isCouponAdded = false,
  setCouponAdded,
  cancelPayment,
}: PaymentStepsProps) => {
  const { cancelCloseModal } = useModalContext();
  const [isBackButtonClicked, setBackButtonClicked] = useState(false);

  const shouldDisplayReviewAndConfirm =
    !isSelectedOfferRecurring && isPayingFromUS;

  const selectedOffer = useSelector(getOrderOffer);
  const isCreditCardPayment = isCreditCardPaymentFlow(selectedOffer);
  const shouldShowCaptcha = !isCreditCardPayment;

  const shouldCreatePayment =
    !displayCoupon && !shouldDisplayReviewAndConfirm && !isCreditCardPayment;

  const PayerInformationStep = {
    id: 'paymentInformation',
    component: (
      <PayerInformation
        next={handleNextClick}
        prev={handlePrevClick}
        skipPaymentCreation={!shouldCreatePayment || isNonAutomatedRecurring}
        shouldShowCaptcha={shouldShowCaptcha}
      />
    ),
  };

  const PaymentOptionsStep = {
    id: 'paymentOptions',
    component: <PaymentOptions onNext={handleNextClick} />,
  };

  const ReviewRecurringPaymentStep = {
    id: 'authenticationStep',
    component: (
      <ReviewRecurringPayment
        onPrev={handlePrevClick}
        onNext={handleNextClick}
      />
    ),
  };

  const TaxesStep = {
    id: 'taxes',
    component: <Taxes onPrev={handlePrevClick} onNext={handleNextClick} />,
  };

  const ReviewAndConfirmStep = {
    id: 'reviewAndConfirm',
    component: (
      <ReviewAndConfirm onPrev={handlePrevClick} onNext={handleNextClick} />
    ),
  };

  const CouponStep = {
    id: 'coupon',
    component: <Coupon />,
  };

  const PaymentInstructionsStep = {
    id: 'instructions',
    component: (
      <InstructionsContainer
        goToStep={goToStep}
        onPrev={restart}
        onClose={onClose}
        onCancel={onCancelPayment}
      />
    ),
  };

  const steps = [
    PaymentOptionsStep,
    PayerInformationStep,
    ...(displayCoupon ? [CouponStep] : []),
    ...(displayTaxes ? [TaxesStep] : []),
    ...(shouldDisplayReviewAndConfirm ? [ReviewAndConfirmStep] : []),
    ...(isSelectedOfferRecurring ? [ReviewRecurringPaymentStep] : []),
    PaymentInstructionsStep,
  ];
  const { index, complete, navigation, reset } = useStep({ steps });

  function handleNextClick() {
    complete(index);
    navigation.next();
  }

  function handlePrevClick() {
    if (isCouponAdded) {
      setBackButtonClicked(true);
    } else {
      goToStep('paymentOptions');
    }
  }

  function restart() {
    onRestart();
    reset();
  }

  function goToStep(step: string) {
    complete(index);
    navigation.go(step);
  }

  function cancelCloseModalFromCouponAdded() {
    setBackButtonClicked(false);
    cancelCloseModal();
  }

  function confirmCloseModalFromCouponAdded() {
    cancelPayment();
    goToStep('paymentOptions');
    setBackButtonClicked(false);
    setCouponAdded(false);
  }

  if (isBackButtonClicked) {
    return (
      <CloseModalConfirmation
        onCancel={() => cancelCloseModalFromCouponAdded()}
        onConfirm={() => confirmCloseModalFromCouponAdded()}
        title="coupon.backButton.modal.title"
        description="coupon.backButton.modal.description"
      />
    );
  }

  return steps[index].component;
};

const mapStateToProps = (state: RootState) => ({
  displayTaxes: paymentIncludesTaxes(state) || orderIncludesTaxes(state),
  isSelectedOfferRecurring: isSelectedOfferRecurring(state),
  isNonAutomatedRecurring: isNonAutomatedRecurring(state),
  displayCoupon: shouldDisplayAddCoupon(state),
  isCouponAdded: isCouponAdded(state),
  isPayingFromUS: isPayingFromUS(state),
});

const mapDispatchToProps = {
  setCouponAdded,
  cancelPayment,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export const PaymentSteps = connector(PaymentStepsComponent);
